import { useMemo, useState } from 'react';

import { Lock, ShieldCheck } from 'lucide-react';

import { ApiError } from '@/api/client';
import type {
  PermissionAction,
  PermissionMatrix,
  PermissionModule,
  RoleRead,
} from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Chip } from '@/components/ui/Chip';
import { ErrorState } from '@/components/ui/EmptyState';
import { ConfirmModal } from '@/components/ui/Modal';
import { SkeletonTable } from '@/components/ui/Skeleton';
import { StatStrip } from '@/components/ui/StatStrip';
import { Table, TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { cx } from '@/lib/cx';
import { useUnsavedChanges } from '@/lib/useUnsavedChanges';

import { useRoleList, useUpdateRole } from '../api';
import styles from './RolesPage.module.css';

const MODULES: { key: PermissionModule; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'candidates', label: 'Candidates' },
  { key: 'screenings', label: 'Screenings' },
  { key: 'ats', label: 'ATS' },
  { key: 'careers', label: 'Careers' },
  { key: 'website', label: 'Website' },
  { key: 'meetings', label: 'Meetings' },
  { key: 'settings', label: 'Settings' },
];

const ACTIONS: { key: PermissionAction; label: string }[] = [
  { key: 'view', label: 'View' },
  { key: 'create', label: 'Create' },
  { key: 'edit', label: 'Edit' },
  { key: 'delete', label: 'Delete' },
  { key: 'export', label: 'Export' },
];

const TOTAL = MODULES.length * ACTIONS.length;

function granted(matrix: PermissionMatrix): number {
  return MODULES.reduce(
    (count, module) =>
      count + ACTIONS.filter((action) => matrix[module.key][action.key]).length,
    0,
  );
}

export function RolesPage() {
  const toast = useToast();
  const roles = useRoleList();
  const updateRole = useUpdateRole();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** The matrix as edited. Null while it matches what the server sent. */
  const [draft, setDraft] = useState<PermissionMatrix | null>(null);
  /**
   * The role waiting behind the discard prompt, once one has been clicked.
   *
   * The id and not a flag: `unsaved.discard()` only resumes a navigation `useBlocker`
   * is holding, and picking another role in the list is not a navigation, so nothing
   * else remembers what was clicked. Answering the prompt would discard the edit and
   * leave the panel on the role being edited, and the click would have to be made
   * again.
   */
  const [confirmLeave, setConfirmLeave] = useState<string | null>(null);

  // Memoised rather than `roles.data ?? []` inline: the fallback is a fresh array on
  // every render, which would make the stats below recompute every time.
  const list = useMemo(() => roles.data ?? [], [roles.data]);
  const selected: RoleRead | undefined =
    list.find((role) => role.id === selectedId) ??
    list.find((role) => !role.is_system) ??
    list[0];

  // Missing modules are denied, including during a frontend/backend rollout.
  const matrix = useMemo<PermissionMatrix | null>(() => {
    if (draft) return draft;
    if (!selected) return null;
    return Object.fromEntries(
      MODULES.map(({ key }) => [
        key,
        Object.fromEntries(
          ACTIONS.map(({ key: action }) => [
            action,
            selected.permissions[key]?.[action] === true,
          ]),
        ),
      ]),
    ) as PermissionMatrix;
  }, [draft, selected]);
  const dirty = draft !== null;

  const unsaved = useUnsavedChanges(dirty);

  // Built by assignment rather than a computed-key spread: a spread widens the key
  // type and the result stops being a PermissionMatrix.
  const replaceModule = (
    module: PermissionModule,
    actions: Record<PermissionAction, boolean>,
  ) => {
    if (!matrix) return;
    const next: PermissionMatrix = { ...matrix };
    next[module] = actions;
    setDraft(next);
  };

  const toggle = (module: PermissionModule, action: PermissionAction) => {
    if (!matrix || !selected || selected.is_system) return;
    const actions: Record<PermissionAction, boolean> = { ...matrix[module] };
    actions[action] = !actions[action];
    replaceModule(module, actions);
  };

  const toggleRow = (module: PermissionModule) => {
    if (!matrix || !selected || selected.is_system) return;
    const allOn = ACTIONS.every((action) => matrix[module][action.key]);
    const actions: Record<PermissionAction, boolean> = { ...matrix[module] };
    for (const action of ACTIONS) actions[action.key] = !allOn;
    replaceModule(module, actions);
  };

  const save = () => {
    if (!selected || !draft) return;
    updateRole.mutate(
      { id: selected.id, permissions: draft },
      {
        onSuccess: () => {
          setDraft(null);
          toast.success(`${selected.name} permissions saved.`);
        },
        onError: (error) => {
          toast.error(error instanceof ApiError ? error.message : 'That did not save.');
        },
      },
    );
  };

  const selectRole = (role: RoleRead) => {
    if (dirty && role.id !== selected?.id) {
      // Switching role would silently drop the edit, so ask.
      setConfirmLeave(role.id);
      return;
    }
    setSelectedId(role.id);
  };

  const stats = useMemo(
    () => [
      { label: 'Roles', value: list.length },
      { label: 'Accounts', value: list.reduce((sum, role) => sum + role.user_count, 0) },
      ...(selected && matrix
        ? [{ label: `${selected.name} permissions`, value: `${granted(matrix)}/${TOTAL}` }]
        : []),
    ],
    [list, selected, matrix],
  );

  if (roles.isPending) {
    return (
      <Page>
        <PageHeader title="Roles & permissions" />
        <Panel flush>
          <SkeletonTable rows={6} columns={6} label="Loading roles" />
        </Panel>
      </Page>
    );
  }

  if (roles.isError) {
    return (
      <Page>
        <PageHeader title="Roles & permissions" />
        <Panel>
          <ErrorState
            message={roles.error instanceof ApiError ? roles.error.message : undefined}
            onRetry={() => {
              void roles.refetch();
            }}
          />
        </Panel>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title="Roles & permissions"
        description="Control what each role can see and do. Changes apply to everyone holding that role, on their very next request."
        actions={
          dirty ? (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setDraft(null);
                }}
              >
                Discard
              </Button>
              <Button loading={updateRole.isPending} onClick={save}>
                Save changes
              </Button>
            </>
          ) : null
        }
      />

      <StatStrip items={stats} />

      <div className={styles.layout}>
        {/* ------------------------------------------------------ list -- */}
        <Panel flush>
          <ul className={styles.roleList} role="list">
            {list.map((role) => (
              <li key={role.id}>
                <button
                  type="button"
                  className={cx(
                    styles.roleButton,
                    role.id === selected?.id && styles.roleActive,
                  )}
                  onClick={() => {
                    selectRole(role);
                  }}
                  aria-current={role.id === selected?.id ? 'true' : undefined}
                >
                  <span className={styles.roleIcon}>
                    <ShieldCheck size={15} />
                  </span>
                  <span className={styles.roleText}>
                    <span className={styles.roleName}>{role.name}</span>
                    <span className={styles.roleCount}>
                      {role.user_count} {role.user_count === 1 ? 'account' : 'accounts'}
                    </span>
                  </span>
                  {role.is_system ? (
                    <Chip size="sm" tone="applicant-arranged" icon={<Lock size={11} />}>
                      Locked
                    </Chip>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        {/* ---------------------------------------------------- matrix -- */}
        <Panel flush>
          {selected && matrix ? (
            <>
              <div className={styles.matrixHead}>
                <div>
                  <h2 className={styles.matrixTitle}>{selected.name}</h2>
                  <p className={styles.matrixDescription}>{selected.description}</p>
                </div>
                {dirty ? (
                  <Chip tone="hold" dot>
                    Unsaved
                  </Chip>
                ) : null}
              </div>

              {selected.is_system ? (
                <div className={styles.lockedNote}>
                  <Alert tone="info">
                    <b>{selected.name} cannot be changed.</b> It is the account of last resort:
                    if its permissions could be narrowed, there would be no way back. The server
                    refuses the change too, so this is not merely a disabled screen.
                  </Alert>
                </div>
              ) : null}

              <TableWrap>
                <Table minWidth={640}>
                  <thead>
                    <tr>
                      <Th label="Module" />
                      {ACTIONS.map((action) => (
                        <Th key={action.key} label={action.label} className={styles.centre} />
                      ))}
                      <Th label="All" className={styles.centre} />
                    </tr>
                  </thead>
                  <tbody>
                    {MODULES.map((module) => {
                      const allOn = ACTIONS.every((action) => matrix[module.key][action.key]);
                      return (
                        <Tr key={module.key}>
                          <Td>
                            <span className={styles.moduleName}>{module.label}</span>
                          </Td>
                          {ACTIONS.map((action) => (
                            <Td key={action.key} className={styles.centre}>
                              <Checkbox
                                checked={matrix[module.key][action.key]}
                                disabled={selected.is_system}
                                aria-label={`${action.label} ${module.label}`}
                                onChange={() => {
                                  toggle(module.key, action.key);
                                }}
                              />
                            </Td>
                          ))}
                          <Td className={styles.centre}>
                            <button
                              type="button"
                              className={styles.allNone}
                              disabled={selected.is_system}
                              onClick={() => {
                                toggleRow(module.key);
                              }}
                            >
                              {allOn ? 'None' : 'All'}
                            </button>
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
            </>
          ) : null}
        </Panel>
      </div>

      {/* Leaving the screen with an edit in hand. */}
      <ConfirmModal
        open={unsaved.blocked || confirmLeave !== null}
        title="Discard your changes?"
        body="These permissions have not been saved. Leaving now loses them."
        confirmLabel="Discard and leave"
        danger
        onCancel={() => {
          setConfirmLeave(null);
          unsaved.stay();
        }}
        onConfirm={() => {
          setDraft(null);
          // The whole point of the click that raised this prompt.
          if (confirmLeave !== null) setSelectedId(confirmLeave);
          setConfirmLeave(null);
          unsaved.discard();
        }}
      />
    </Page>
  );
}
